import React, { useEffect, useRef } from 'react';
import { X, Shield, FileText, ExternalLink } from 'lucide-react';

// ─── section components ────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
    <div className="mb-7">
        <h2 className="text-[13.5px] font-bold text-[#0F172A] font-dmsans mb-2.5 pb-2 border-b border-[#EFF1F5]">
            {title}
        </h2>
        <div className="text-[13px] text-[#475569] font-dmsans leading-[1.75] space-y-2.5">
            {children}
        </div>
    </div>
);

const Ul = ({ items }) => (
    <ul className="space-y-1.5 pl-1">
        {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2">
                <span className="mt-[5px] w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#3F57E0' }} />
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const Strong = ({ children }) => (
    <strong className="font-semibold text-[#0F172A]">{children}</strong>
);

// ─── content ──────────────────────────────────────────────────────────────────

const PRIVACY_SECTIONS = [
    {
        title: '1. Introduction',
        content: (
            <>
                <p>Career Champ ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and your rights regarding your data.</p>
                <p>By using Career Champ, you agree to the collection and use of information in accordance with this policy.</p>
            </>
        ),
    },
    {
        title: '2. Information We Collect',
        content: (
            <>
                <p><Strong>Account information:</Strong> Name, email address, password (hashed), and profile photo when you register.</p>
                <p><Strong>Google account data:</Strong> If you sign in with Google, we receive your name, email, and profile photo. With your permission, we store Google OAuth tokens to send emails on your behalf via the Gmail API.</p>
                <p><Strong>LinkedIn account data:</Strong> If you connect LinkedIn, we store an account identifier and session token to perform actions (connection requests, messages) on your behalf.</p>
                <p><Strong>Resume / CV data:</Strong> If you upload a CV, we parse and store its contents to personalise your experience.</p>
                <p><Strong>Usage data:</Strong> Pages visited, features used, search queries, and actions taken within the platform.</p>
                <p><Strong>Payment data:</Strong> Billing is handled by Stripe. We do not store your card details. We receive and store subscription status and payment history.</p>
            </>
        ),
    },
    {
        title: '3. How We Use Your Information',
        content: (
            <Ul items={[
                'To provide and personalise the Service (job matching, recruiter suggestions, hiring signals)',
                'To generate AI-powered outreach messages and strategy advice using your profile',
                'To send transactional emails (OTP verification, password reset, account notifications)',
                'To process payments and manage your subscription',
                'To connect your LinkedIn account and perform actions you authorise',
                'To improve the platform through anonymised usage analytics',
                'To comply with legal obligations',
            ]} />
        ),
    },
    {
        title: '4. Third-Party Services',
        content: (
            <>
                <p>To operate the Service, we work with trusted third-party providers in the following categories:</p>
                <Ul items={[
                    'Payment processing',
                    'Authentication (Google OAuth)',
                    'LinkedIn account integration',
                    'AI-powered content generation and analysis',
                    'Cloud database and application hosting',
                ]} />
                <p>Each provider processes only the data necessary to perform their function. We do not sell your personal data to third parties.</p>
            </>
        ),
    },
    {
        title: '5. Data Retention',
        content: (
            <>
                <p>We retain your account data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it for legal or billing purposes.</p>
                <p>Anonymised or aggregated data may be retained indefinitely for analytics purposes.</p>
            </>
        ),
    },
    {
        title: '6. Cookies and Local Storage',
        content: (
            <p>Career Champ uses browser local storage to maintain your login session (JWT token) and cache certain preferences. We do not use third-party tracking cookies.</p>
        ),
    },
    {
        title: '7. Data Security',
        content: (
            <p>We take reasonable technical and organisational measures to protect your data, including encrypted connections (HTTPS), hashed passwords, and access controls. However, no method of transmission over the internet is 100% secure.</p>
        ),
    },
    {
        title: '8. Your Rights',
        content: (
            <>
                <p>Depending on your location, you may have the following rights:</p>
                <Ul items={[
                    'Access — Request a copy of the personal data we hold about you',
                    'Correction — Request correction of inaccurate data',
                    'Deletion — Request deletion of your account and personal data',
                    'Portability — Request your data in a portable format',
                    'Objection — Object to certain types of processing',
                ]} />
                <p>To exercise any of these rights, contact us at <a href="mailto:info@careerchamp.io" className="text-[#3F57E0] hover:underline font-medium">info@careerchamp.io</a></p>
            </>
        ),
    },
    {
        title: "9. Children's Privacy",
        content: (
            <p>Career Champ is not directed at children under the age of 18. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us and we will delete it promptly.</p>
        ),
    },
    {
        title: '10. Changes to This Policy',
        content: (
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes via email or an in-app notice. The "Last updated" date at the top of this page indicates when the policy was last revised.</p>
        ),
    },
    {
        title: '11. Contact Us',
        content: (
            <div className="rounded-xl p-4 text-[12.5px]" style={{ background: '#F8FAFC', border: '1px solid #E6E8EE' }}>
                <p className="font-semibold text-[#0F172A] font-dmsans mb-1">Career Champ</p>
                <p>Email: <a href="mailto:info@careerchamp.io" className="text-[#3F57E0] hover:underline">info@careerchamp.io</a></p>
                <p>Website: <a href="https://careerchamp.io" className="text-[#3F57E0] hover:underline" target="_blank" rel="noreferrer">careerchamp.io</a></p>
            </div>
        ),
    },
];

const TERMS_SECTIONS = [
    {
        title: '1. Acceptance of Terms',
        content: (
            <>
                <p>By accessing or using Career Champ ("the Service"), you agree to be bound by these Terms of Service.</p>
                <p>These terms apply to all users, including visitors, registered users, and paying subscribers.</p>
            </>
        ),
    },
    {
        title: '2. Description of Service',
        content: (
            <p>Career Champ is a job search platform that helps users discover companies, find recruiters, track hiring signals, generate AI-powered outreach messages, and manage their job search pipeline. The Service integrates with LinkedIn, Google, and other third-party providers.</p>
        ),
    },
    {
        title: '3. Account Registration',
        content: (
            <>
                <p>You may register using an email address and password or via Google OAuth. You are responsible for:</p>
                <Ul items={[
                    'Providing accurate and complete registration information',
                    'Maintaining the security of your account credentials',
                    'All activity that occurs under your account',
                ]} />
                <p>We reserve the right to suspend or terminate accounts that violate these terms.</p>
            </>
        ),
    },
    {
        title: '4. Credits and Subscriptions',
        content: (
            <>
                <p>Career Champ operates on a credit-based system. Credits are consumed when you search for companies, reveal recruiter contacts, and use AI features. Free accounts receive a starting credit balance.</p>
                <p>Paid subscriptions and credit top-ups are processed through Stripe. All payments are non-refundable unless otherwise required by applicable law. Subscription plans renew automatically unless cancelled before the renewal date.</p>
            </>
        ),
    },
    {
        title: '5. LinkedIn Integration',
        content: (
            <>
                <p>Career Champ connects to LinkedIn through Unipile. By connecting your LinkedIn account, you authorise Career Champ to send connection requests, direct messages, and perform searches on your behalf as directed by you.</p>
                <p>You are solely responsible for ensuring your use of LinkedIn features complies with LinkedIn's own Terms of Service. Career Champ is not affiliated with LinkedIn.</p>
            </>
        ),
    },
    {
        title: '6. AI-Generated Content',
        content: (
            <p>Career Champ uses AI to generate outreach messages, strategy advice, and hiring signal analysis. AI-generated content is provided as a starting point and may not always be accurate or appropriate. You are responsible for reviewing and editing any content before sending it.</p>
        ),
    },
    {
        title: '7. Acceptable Use',
        content: (
            <>
                <p>You agree not to:</p>
                <Ul items={[
                    'Use the Service for spam, harassment, or unsolicited mass outreach',
                    'Attempt to reverse-engineer, scrape, or misuse the platform',
                    'Violate any applicable law or regulation',
                    'Share your account credentials with others',
                    "Use the Service in any way that violates LinkedIn's or any third-party's terms",
                ]} />
            </>
        ),
    },
    {
        title: '8. Intellectual Property',
        content: (
            <>
                <p>All content, design, and software comprising the Service are owned by Career Champ or its licensors. You may not copy, reproduce, or distribute any part of the Service without written permission.</p>
                <p>You retain ownership of any content you submit (e.g., resume, profile data). By submitting content, you grant Career Champ a limited licence to process it in order to provide the Service.</p>
            </>
        ),
    },
    {
        title: '9. Disclaimers',
        content: (
            <p>The Service is provided "as is" without warranties of any kind. Career Champ does not guarantee job placement, recruiter responses, or hiring outcomes. Third-party data is sourced from external providers and may not always be accurate or up to date.</p>
        ),
    },
    {
        title: '10. Limitation of Liability',
        content: (
            <p>To the maximum extent permitted by law, Career Champ shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including but not limited to loss of data, lost profits, or reputational harm.</p>
        ),
    },
    {
        title: '11. Termination',
        content: (
            <p>We reserve the right to suspend or terminate your access at any time for violations of these terms. You may delete your account at any time by contacting us. Upon termination, your data will be handled in accordance with our Privacy Policy.</p>
        ),
    },
    {
        title: '12. Changes to Terms',
        content: (
            <p>We may update these Terms of Service from time to time. We will notify users of material changes via email or an in-app notice. Continued use of the Service after changes constitutes acceptance of the updated terms.</p>
        ),
    },
    {
        title: '13. Governing Law',
        content: (
            <p>These terms are governed by the laws of the United States. Any disputes shall be resolved in the courts of competent jurisdiction.</p>
        ),
    },
    {
        title: '14. Contact',
        content: (
            <p>For questions about these Terms of Service, contact us at <a href="mailto:info@careerchamp.io" className="text-[#3F57E0] hover:underline font-medium">info@careerchamp.io</a></p>
        ),
    },
];

// ─── modal ────────────────────────────────────────────────────────────────────

const LegalModal = ({ type, onClose }) => {
    const bodyRef = useRef(null);
    const isPrivacy = type === 'privacy';

    const title       = isPrivacy ? 'Privacy Policy'   : 'Terms of Service';
    const subtitle    = isPrivacy ? 'How we handle your data' : 'Rules governing your use of Career Champ';
    const Icon        = isPrivacy ? Shield : FileText;
    const sections    = isPrivacy ? PRIVACY_SECTIONS : TERMS_SECTIONS;
    const pageUrl     = isPrivacy ? '/privacy' : '/terms';

    // scroll body to top when type switches
    useEffect(() => { bodyRef.current?.scrollTo(0, 0); }, [type]);

    // close on Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    if (!type) return null;

    return (
        <div
            className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
            style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl mx-0 sm:mx-4 flex flex-col overflow-hidden"
                style={{ maxHeight: '88vh' }}>

                {/* ── Header ───────────────────────────────── */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-[#EFF1F5] flex-shrink-0">
                    <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: '#EEF1FE' }}
                    >
                        <Icon size={16} style={{ color: '#3F57E0' }} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <div className="text-[14.5px] font-bold text-[#0F172A] font-dmsans leading-tight">{title}</div>
                        <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-0.5">{subtitle}</div>
                    </div>

                    {/* last updated badge */}
                    <span
                        className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-semibold font-dmsans flex-shrink-0"
                        style={{ background: '#F0FDF4', color: '#16A34A' }}
                    >
                        Last updated: May 2026
                    </span>

                    <button
                        type="button"
                        onClick={onClose}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-[#94A3B8] hover:bg-[#F4F5F8] hover:text-[#334155] transition-colors flex-shrink-0 ml-1"
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* ── Body ────────────────────────────────── */}
                <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 sm:px-7 py-6">
                    {sections.map((s) => (
                        <Section key={s.title} title={s.title}>{s.content}</Section>
                    ))}
                </div>

                {/* ── Footer ──────────────────────────────── */}
                <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-[#EFF1F5] flex-shrink-0" style={{ background: '#FAFBFD' }}>
                    <a
                        href={pageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-medium font-dmsans text-[#94A3B8] hover:text-[#3F57E0] transition-colors"
                    >
                        <ExternalLink size={11} /> Open full page
                    </a>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2 rounded-lg text-[12.5px] font-semibold font-dmsans text-white hover:opacity-90 transition-opacity"
                        style={{ background: '#3F57E0' }}
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LegalModal;
